<script setup>
import { ref } from "vue";
import SectionHeading from "../ui/SectionHeading.vue";
import IconBell from "../icons/IconBell.vue";
import { resolveIcon } from "../icons";
import { useLanding } from "../../composables/useLanding";
import { useStaggerReveal } from "../../composables/useStaggerReveal";
import { useSpotlight } from "../../composables/useSpotlight";

const grid = ref(null);
const { revealed } = useStaggerReveal(grid);
useSpotlight(grid);

const content = useLanding().valueProps;

// Sample data for the tile illustrations, per page — drawings, not claims.
const { slots, avatars } = content.art;

function slotCount(slot) {
  if (slot.status) return slot.status;
  return slot.taken === slot.of ? content.art.fullLabel : `${slot.taken}/${slot.of}`;
}

const week = [
  { day: "א", date: 26, dots: 2 },
  { day: "ב", date: 27, dots: 3 },
  { day: "ג", date: 28, dots: 1 },
  { day: "ד", date: 29, dots: 3, active: true },
  { day: "ה", date: 30, dots: 2 },
  { day: "ו", date: 1, dots: 1 },
];

const bars = [38, 52, 45, 63, 58, 74, 70, 88];
</script>

<template>
  <section class="section section--canvas props-section" id="benefits">
    <div class="container">
      <SectionHeading
        :eyebrow="content.eyebrow"
        :title="content.title"
        :subtitle="content.subtitle"
      />

      <div ref="grid" class="bento">
        <article
          v-for="(prop, i) in content.items"
          :key="prop.title"
          class="tile reveal spotlight"
          :class="[
            `tile--${prop.visual}`,
            { 'is-revealed': revealed, 'spotlight--on-ink': prop.visual === 'schedule' },
          ]"
          :style="{ '--reveal-index': i }"
        >
          <div class="tile__copy">
            <span class="tile__icon">
              <component :is="resolveIcon(prop.icon)" />
            </span>
            <h3 class="tile__title">{{ prop.title }}</h3>
            <p class="tile__text">{{ prop.text }}</p>
          </div>

          <!-- Illustrations are decoration; the copy above carries the meaning. -->
          <div class="tile__art" aria-hidden="true">
            <ul v-if="prop.visual === 'schedule'" class="art-slots">
              <li v-for="slot in slots" :key="slot.time" class="art-slot">
                <span class="art-slot__time">{{ slot.time }}</span>
                <span class="art-slot__name">{{ slot.name }}</span>
                <span
                  class="art-slot__count"
                  :class="{ 'is-full': slot.taken === slot.of }"
                >
                  {{ slotCount(slot) }}
                </span>
                <span class="art-slot__track">
                  <span
                    class="art-slot__fill"
                    :style="{ '--fill': slot.taken / slot.of }"
                  />
                </span>
              </li>
            </ul>

            <div v-else-if="prop.visual === 'capacity'" class="art-capacity">
              <div class="art-capacity__row">
                <span class="art-avatars">
                  <span v-for="a in avatars" :key="a" class="art-avatar">{{ a }}</span>
                  <span class="art-avatar art-avatar--more" dir="ltr">+6</span>
                </span>
                <span class="art-chip art-chip--full">{{ content.art.capacityChip }}</span>
              </div>
              <div class="art-toast">
                <span class="art-toast__icon"><IconBell /></span>
                <span class="art-toast__body">
                  <strong>{{ content.art.toast.title }}</strong>
                  <span>{{ content.art.toast.text }}</span>
                </span>
              </div>
            </div>

            <div v-else-if="prop.visual === 'calendar'" class="art-week">
              <span
                v-for="d in week"
                :key="d.day"
                class="art-day"
                :class="{ 'is-active': d.active }"
              >
                <span class="art-day__name">{{ d.day }}</span>
                <span class="art-day__date">{{ String(d.date).padStart(2, "0") }}</span>
                <span class="art-day__dots">
                  <i v-for="n in d.dots" :key="n" />
                </span>
              </span>
            </div>

            <div v-else-if="prop.visual === 'progress'" class="art-chart">
              <span
                v-for="(h, b) in bars"
                :key="b"
                class="art-bar"
                :class="{ 'is-last': b === bars.length - 1 }"
                :style="{ '--h': h / 100, '--bar-index': b }"
              />
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ------------------------------------------------------------------ grid */

/*
 * A bento layout: two wide tiles and two narrow ones, so the four points
 * read as a composition rather than a row of identical cards.
 */
.bento {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: 1fr;
}

@media (min-width: 640px) {
  .bento {
    grid-template-columns: repeat(2, 1fr);
  }

  .tile--schedule,
  .tile--progress {
    grid-column: span 2;
  }
}

@media (min-width: 960px) {
  .bento {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-5);
  }

  .tile--schedule,
  .tile--progress {
    grid-column: span 2;
  }

  .tile--capacity,
  .tile--calendar {
    grid-column: span 1;
  }
}

/* ------------------------------------------------------------------ tile */

.tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  overflow: hidden;
  padding: var(--space-6);
  border: var(--border);
  border-radius: var(--radius-2xl);
  background: var(--surface);
  transition: transform var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-in-out);
}

/* Wide tiles put the copy and the drawing side by side. */
@media (min-width: 640px) {
  .tile--schedule,
  .tile--progress {
    flex-direction: row;
    align-items: center;
  }

  .tile--schedule .tile__copy,
  .tile--progress .tile__copy {
    flex: 1 1 0;
  }

  .tile--schedule .tile__art,
  .tile--progress .tile__art {
    flex: 1.15 1 0;
  }
}

.tile__copy {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
}

.tile__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 44px;
  block-size: 44px;
  border-radius: var(--radius-md);
  background: var(--brand-soft);
  color: var(--brand);
  transition: transform var(--dur-base) var(--ease-spring);
}

.tile__icon :deep(svg) {
  inline-size: 22px;
  block-size: 22px;
}

.tile__title {
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
}

.tile__text {
  margin: 0;
  color: var(--ink-muted);
  max-inline-size: 42ch;
}

.tile__art {
  position: relative;
}

/* Tile colourways: solid fills. The dark tile is the one that leads. */
.tile--schedule {
  background: var(--ink);
  border-color: transparent;
}

.tile--schedule .tile__title {
  color: var(--on-ink);
}

.tile--schedule .tile__text {
  color: var(--on-ink-muted);
}

.tile--schedule .tile__icon {
  background: rgba(255, 255, 255, 0.1);
  color: var(--on-ink);
}

.tile--capacity {
  background: var(--surface);
}

.tile--capacity .tile__icon {
  background: var(--accent-soft);
  color: var(--accent);
}

.tile--calendar {
  background: var(--surface);
}

.tile--progress {
  background: var(--surface);
}

/* Narrow tiles push the drawing to the bottom so the pair lines up. */
.tile--capacity .tile__art,
.tile--calendar .tile__art {
  margin-block-start: auto;
}

/* -------------------------------------------------------- art: schedule */

.art-slots {
  display: grid;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.art-slot {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--space-1) var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--on-ink);
  font-size: var(--text-sm);
  transition: transform var(--dur-base) var(--ease-out);
}

.tile:hover .art-slot:nth-child(2) {
  transform: translateX(-6px);
}

.art-slot__time {
  font-variant-numeric: tabular-nums;
  font-weight: var(--weight-bold);
}

.art-slot__name {
  color: var(--on-ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.art-slot__count {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--on-ink-muted);
}

.art-slot__count.is-full {
  padding: 2px var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--success-soft);
  color: var(--success);
}

.art-slot__track {
  grid-column: 1 / -1;
  block-size: 4px;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

/* Fills grow in when the tile is revealed. Transform only. */
.art-slot__fill {
  display: block;
  block-size: 100%;
  border-radius: inherit;
  background: var(--brand);
  transform-origin: right;
  transform: scaleX(0);
  transition: transform var(--dur-count) var(--ease-out) 300ms;
}

.is-revealed .art-slot__fill,
html:not(.js) .art-slot__fill {
  transform: scaleX(var(--fill));
}

/* -------------------------------------------------------- art: capacity */

.art-capacity {
  display: grid;
  gap: var(--space-3);
}

.art-capacity__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.art-avatars {
  display: flex;
}

.art-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 34px;
  block-size: 34px;
  border-radius: 50%;
  border: 2px solid var(--surface);
  background: var(--brand-soft);
  color: var(--brand-ink);
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
}

.art-avatar + .art-avatar {
  margin-inline-start: -10px;
}

.art-avatar:nth-child(2) {
  background: var(--accent-soft);
  color: var(--accent);
}

.art-avatar:nth-child(3) {
  background: var(--success-soft);
  color: var(--success);
}

.art-avatar--more {
  background: var(--ink);
  color: var(--on-ink);
}

.art-chip {
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-pill);
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
}

.art-chip--full {
  background: var(--success-soft);
  color: var(--success);
}

.art-toast {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border: var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--lift-2);
  /* Arrives once the bars have filled: the seat that just opened up. */
  opacity: 0;
  translate: 0 16px;
  transition: transform var(--dur-base) var(--ease-spring),
    opacity var(--dur-slow) var(--ease-out) 1300ms,
    translate var(--dur-slow) var(--ease-spring) 1300ms;
}

.is-revealed .art-toast,
html:not(.js) .art-toast {
  opacity: 1;
  translate: 0 0;
}

.tile:hover .art-toast {
  transform: translate3d(0, -4px, 0);
}

.art-toast__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 34px;
  block-size: 34px;
  border-radius: var(--radius-md);
  background: var(--accent);
  color: var(--on-brand);
}

.art-toast__icon :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
}

.art-toast__body {
  display: flex;
  flex-direction: column;
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
}

.art-toast__body strong {
  color: var(--ink);
}

.art-toast__body span {
  color: var(--ink-subtle);
  font-size: var(--text-xs);
}

/* -------------------------------------------------------- art: calendar */

.art-week {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-2);
}

.art-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding-block: var(--space-2);
  border: var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
  transition: transform var(--dur-base) var(--ease-spring);
}

.art-day__name {
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}

.art-day__date {
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.art-day__dots {
  display: flex;
  gap: 2px;
  block-size: 4px;
}

.art-day__dots i {
  inline-size: 4px;
  block-size: 4px;
  border-radius: 50%;
  background: var(--line-strong);
}

.art-day.is-active {
  border-color: transparent;
  background: var(--brand);
  box-shadow: var(--lift-2);
}

.art-day.is-active .art-day__name,
.art-day.is-active .art-day__date {
  color: var(--on-brand);
}

.art-day.is-active .art-day__dots i {
  background: rgba(255, 255, 255, 0.7);
}

.tile:hover .art-day.is-active {
  transform: translate3d(0, -4px, 0);
}

/* -------------------------------------------------------- art: progress */

/* Charts read left to right even on a Hebrew page: growth rises rightward. */
.art-chart {
  direction: ltr;
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  block-size: 140px;
  padding: var(--space-4);
  border: var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
}

.art-bar {
  flex: 1;
  block-size: 100%;
  border-radius: var(--radius-sm) var(--radius-sm) 4px 4px;
  background: var(--brand-soft);
  transform-origin: bottom;
  transform: scaleY(0.08);
  transition: transform var(--dur-count) var(--ease-out);
  transition-delay: calc(300ms + var(--bar-index) * var(--stagger));
}

.art-bar.is-last {
  background: var(--brand);
}

.is-revealed .art-bar,
html:not(.js) .art-bar {
  transform: scaleY(var(--h));
}

@media (prefers-reduced-motion: reduce) {
  .art-toast {
    transition-delay: 0ms;
  }
}

/* ---------------------------------------------------------------- mobile */

@media (max-width: 639px) {
  .tile {
    padding: var(--space-5);
    border-radius: var(--radius-xl);
  }

  .art-chart {
    block-size: 110px;
  }
}
</style>
